import React, { useMemo, useState, useEffect } from 'react';
import Select from 'react-select';

type TagTypesPayload = {
  tag_types?: Array<{
    tag_type: string;
    values?: string[];
  }>;
};

type TagsFilterInlineProps = {
  placeholder?: string;
  fetchOptionsApi?: string | any;
  initialValue?: any;
  onApply: (value: string | null) => void;
  queryKey: string;
};

const TagsFilterInline: React.FC<TagsFilterInlineProps> = ({
  placeholder,
  fetchOptionsApi,
  initialValue,
  onApply,
  queryKey,
}) => {
  const basePayload: TagTypesPayload | null = useMemo(() => {
    try {
      if (!fetchOptionsApi) return null;
      if (typeof fetchOptionsApi === 'string') {
        const maybe = JSON.parse(fetchOptionsApi);
        if (Array.isArray(maybe)) return null;
        return maybe as TagTypesPayload;
      }
      return fetchOptionsApi as TagTypesPayload;
    } catch {
      return null;
    }
  }, [fetchOptionsApi]);

  const parseInitial = (
    initialValue: any
  ): {
    logic: 'AND' | 'OR';
    selectedTypes: string[];
    selectedValues: string[];
  } => {
    try {
      if (!initialValue)
        return { logic: 'AND', selectedTypes: [], selectedValues: [] };
      const obj =
        typeof initialValue === 'string'
          ? JSON.parse(initialValue)
          : initialValue;
      const logic = obj?.logic === 'OR' ? 'OR' : 'AND';
      const filters = Array.isArray(obj?.filters) ? obj.filters : [];
      const selectedTypes: string[] = [];
      const selectedValues: string[] = [];
      filters.forEach((f: any) => {
        if (typeof f?.tag_type === 'string')
          selectedTypes.push(String(f.tag_type));
        if (typeof f?.tag_value === 'string')
          selectedValues.push(String(f.tag_value));
      });
      return { logic, selectedTypes, selectedValues };
    } catch {
      return { logic: 'AND', selectedTypes: [], selectedValues: [] };
    }
  };

  const {
    logic: initLogic,
    selectedTypes: initTypes,
    selectedValues: initValues,
  } = useMemo(() => parseInitial(initialValue), [initialValue]);

  const [logic, setLogic] = useState<'AND' | 'OR'>(initLogic);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initTypes);
  const [selectedValues, setSelectedValues] = useState<string[]>(initValues);

  // Track applied state to avoid calling API on every change
  const [hasChanges, setHasChanges] = useState(false);

  // Reset when initialValue changes
  useEffect(() => {
    const parsed = parseInitial(initialValue);
    setLogic(parsed.logic);
    setSelectedTypes(parsed.selectedTypes);
    setSelectedValues(parsed.selectedValues);
    setHasChanges(false);
  }, [initialValue]);

  const typeOptions = useMemo(() => {
    const types: string[] = (basePayload?.tag_types || []).map(t =>
      String(t.tag_type)
    );
    return types.map(t => ({ value: t, label: t }));
  }, [basePayload]);

  // Group values by their tag type
  const valueOptionsByType = useMemo(() => {
    const byType = new Map<string, string[]>();
    (basePayload?.tag_types || []).forEach(t => {
      if (!t || !t.tag_type) return;
      byType.set(
        String(t.tag_type),
        (t.values || []).map(v => String(v))
      );
    });
    return byType;
  }, [basePayload]);

  // Create grouped value options
  const groupedValueOptions = useMemo(() => {
    const typesToUse =
      selectedTypes.length > 0
        ? selectedTypes
        : Array.from(valueOptionsByType.keys());
    const groups: Array<{
      label: string;
      options: Array<{ value: string; label: string }>;
    }> = [];

    typesToUse.forEach(type => {
      const values = valueOptionsByType.get(type) || [];
      if (values.length > 0) {
        groups.push({
          label: type,
          options: values.map(v => ({ value: v, label: v })),
        });
      }
    });

    return groups;
  }, [valueOptionsByType, selectedTypes]);

  const applyChanges = () => {
    if (selectedTypes.length === 0 && selectedValues.length === 0) {
      onApply(null);
      setHasChanges(false);
      return;
    }
    const filters: any[] = [];
    selectedTypes.forEach(t => filters.push({ tag_type: t }));
    selectedValues.forEach(v => filters.push({ tag_value: v }));
    const payload = { filters, logic };
    onApply(JSON.stringify(payload));
    setHasChanges(false);
  };

  const clear = () => {
    setSelectedTypes([]);
    setSelectedValues([]);
    setLogic('AND');
    setHasChanges(true);
  };

  const handleTypeChange = (vals: any) => {
    setSelectedTypes((vals || []).map((v: any) => String(v.value)));
    setHasChanges(true);
  };

  const handleValueChange = (vals: any) => {
    setSelectedValues((vals || []).map((v: any) => String(v.value)));
    setHasChanges(true);
  };

  const handleLogicChange = (newLogic: 'AND' | 'OR') => {
    setLogic(newLogic);
    setHasChanges(true);
  };

  const containerStyle: React.CSSProperties = {
    padding: 16,
    width: 360,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: 12,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 500,
    marginBottom: 4,
    color: '#374151',
  };

  const logicButtonStyle: React.CSSProperties = {
    padding: '4px 12px',
    borderRadius: 6,
    border: '1px solid #D1D5DB',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 12,
    marginRight: 8,
  };

  const activeLogicStyle: React.CSSProperties = {
    ...logicButtonStyle,
    background: '#111827',
    color: 'white',
    border: '1px solid #111827',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 12,
  };

  const applyButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: hasChanges ? '#111827' : '#6B7280',
    color: 'white',
    marginLeft: 8,
    cursor: hasChanges ? 'pointer' : 'default',
  };

  const clearButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    background: 'transparent',
    border: '1px solid #D1D5DB',
    color: '#374151',
  };

  const infoStyle: React.CSSProperties = {
    fontSize: 11,
    color: '#6B7280',
    lineHeight: 1.4,
    marginBottom: 12,
    padding: '8px 12px',
    background: '#F9FAFB',
    borderRadius: 6,
  };

  // Get flat value options for display
  const allValueOptions = groupedValueOptions.flatMap(g => g.options);

  return (
    <div style={containerStyle} onClick={e => e.stopPropagation()}>
      <div style={infoStyle}>
        Select tag types to filter by type only, or choose specific values.
        Values are grouped by their tag type. Click Apply to update the filter.
      </div>

      <div style={sectionStyle}>
        <div style={labelStyle}>Tag Types</div>
        <Select
          isMulti
          placeholder="Select types..."
          options={typeOptions}
          value={typeOptions.filter(o =>
            selectedTypes.includes(String(o.value))
          )}
          onChange={handleTypeChange}
          closeMenuOnSelect={false}
        />
      </div>

      <div style={sectionStyle}>
        <div style={labelStyle}>
          Values{' '}
          {selectedTypes.length > 0 && `(from ${selectedTypes.join(', ')})`}
        </div>
        <Select
          isMulti
          placeholder={
            selectedTypes.length
              ? 'Select values...'
              : 'Select types first or choose from all...'
          }
          options={groupedValueOptions}
          value={allValueOptions.filter(o =>
            selectedValues.includes(String(o.value))
          )}
          onChange={handleValueChange}
          closeMenuOnSelect={false}
          formatGroupLabel={(group: any) => (
            <div
              style={{
                fontWeight: 600,
                fontSize: 11,
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {group.label}
            </div>
          )}
        />
      </div>

      <div style={sectionStyle}>
        <div style={labelStyle}>Combine conditions with</div>
        <button
          onClick={() => handleLogicChange('AND')}
          style={logic === 'AND' ? activeLogicStyle : logicButtonStyle}
        >
          AND (all conditions)
        </button>
        <button
          onClick={() => handleLogicChange('OR')}
          style={logic === 'OR' ? activeLogicStyle : logicButtonStyle}
        >
          OR (any condition)
        </button>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 16,
        }}
      >
        <button onClick={clear} style={clearButtonStyle}>
          Clear
        </button>
        <button
          onClick={applyChanges}
          style={applyButtonStyle}
          disabled={!hasChanges}
        >
          Apply{hasChanges && ' *'}
        </button>
      </div>
    </div>
  );
};

export default TagsFilterInline;
